import type { Label, LabelType } from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  MultiSelect,
  MultiSelectComponents,
  type MultiSelectOption,
  type MultiSelectProps,
  type OnChangeRequest,
  SelectCheck,
} from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaPlus } from 'react-icons/fa';
import { useLabelsQuery } from '../../graphql/hooks/useLabelsQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { ManageLabelMenu } from './ManageLabelMenu';

const labelToOption = (label: Label): MultiSelectOption<Label> => {
  return {
    label: label.name,
    value: label.id,
    raw: label,
  };
};

export interface LabelsControlProps
  extends Omit<MultiSelectProps<Label>, 'options' | 'selectedValues'> {
  onChange: (request: OnChangeRequest<Label>) => void;
  onDelete?: (labelId: string) => void;
  value: string[];
  type: LabelType;
  canManage: boolean;
}

export const LabelsControl = (props: LabelsControlProps) => {
  const { onChange, onDelete, isLoading, isDisabled, value, variant, type, canManage, ...rest } =
    props;
  const { organization } = useAppContext();

  const labelsQuery = useLabelsQuery({
    organizationId: organization.id,
    type,
  });

  const options: MultiSelectOption<Label>[] = React.useMemo(() => {
    const labels = (labelsQuery?.data?.organization?.labels as Label[]) || [];
    return labels.map(labelToOption);
  }, [labelsQuery]);

  const selectedValues = React.useMemo(() => {
    const values: MultiSelectOption<Label>[] = value.flatMap((v) =>
      options.filter((o) => o.value === v),
    );
    return values;
  }, [options, value]);

  return (
    <MultiSelect
      {...rest}
      options={options}
      selectedValues={selectedValues}
      isLoading={isLoading}
      isCreatable={canManage}
      isDisabled={isDisabled}
      onChange={onChange}
      variant={variant}
      placeholder='Add a label'
      size='sm'
      bg='transparent'
      components={{
        Value: (props) => (
          <ChakraV3.Tag
            size='md'
            colorPalette={props.option.raw.color}
            onClose={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange({
                value: props.option,
                values: selectedValues.filter((v) => v.value !== props.option.value),
                action: 'REMOVE',
              });
            }}
          >
            {props.option.label}
          </ChakraV3.Tag>
        ),

        Option: (props) => {
          const isNewLabel = P.isString(props.option.raw) && props.option.raw === 'CREATABLE';

          const text = isNewLabel ? (
            <>
              Create label
              <ChakraV3.Text fontWeight='bold'>&nbsp;{`"${props.option.label}"`}</ChakraV3.Text>
            </>
          ) : (
            <ChakraV3.Text>{props.option.label}</ChakraV3.Text>
          );

          return (
            <ChakraV3.HStack w='full'>
              <ChakraV3.Tag
                size='md'
                colorPalette={props.option.raw.color ?? 'blue'}
                maxW='100%'
                startElement={
                  isNewLabel && (
                    <ChakraV3.Icon display='flex'>
                      <FaPlus />
                    </ChakraV3.Icon>
                  )
                }
                labelProps={{
                  display: 'inline-flex',
                  lineHeight: 'normal',
                }}
              >
                {props.option.raw.description ? (
                  <ChakraV3.Tooltip content={props.option.raw.description}>{text}</ChakraV3.Tooltip>
                ) : (
                  text
                )}
              </ChakraV3.Tag>
              <ChakraV3.Spacer />
              <SelectCheck />
              {canManage && !isNewLabel && (
                <ManageLabelMenu label={props.option.raw} onDelete={onDelete} />
              )}
            </ChakraV3.HStack>
          );
        },
        Footer: () => (
          <MultiSelectComponents.Footer>
            <ChakraV3.Text color='gray.500' fontSize='xs' fontWeight='semibold'>
              {canManage ? 'Select a label or create one' : 'Select a label'}
            </ChakraV3.Text>
          </MultiSelectComponents.Footer>
        ),
      }}
    />
  );
};
